import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, ApiClientError } from "../lib/api";
import { customerLabel, dateTime, titleCase, vehicleLabel } from "../lib/format";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/Layout";
import { DataState, ErrorBanner, Notice } from "../components/ui";
import type { RepairOrder, RepairOrderStatus } from "../lib/types";

type Board = { lanes: Array<{ status: RepairOrderStatus; orders: RepairOrder[] }> };

/** The next sensible lane for a one-click advance. */
const NEXT_LANE: Partial<Record<RepairOrderStatus, RepairOrderStatus>> = {
  SCHEDULED: "CHECKED_IN",
  CHECKED_IN: "DIAGNOSING",
  DIAGNOSING: "AWAITING_APPROVAL",
  AWAITING_APPROVAL: "IN_PROGRESS",
  AWAITING_PARTS: "IN_PROGRESS",
  IN_PROGRESS: "QUALITY_CHECK",
  QUALITY_CHECK: "READY",
  READY: "DELIVERED",
};

export function WorkBoardPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const scrollerRef = useRef<HTMLDivElement>(null);

  const scrollBy = (delta: number) =>
    scrollerRef.current?.scrollBy({ left: delta, behavior: "smooth" });

  const board = useQuery({
    queryKey: ["board"],
    queryFn: () => api.get<Board>("/repair-orders/board"),
    refetchInterval: 30_000,
  });

  const advance = useMutation({
    mutationFn: (vars: { id: string; status: RepairOrderStatus; version: number }) =>
      api.post<RepairOrder & { warnings?: string[] }>(`/repair-orders/${vars.id}/status`, {
        status: vars.status,
        // Optimistic lock so two advisors cannot silently overwrite each other.
        expectedVersion: vars.version,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["board"] }),
  });

  const conflict = advance.error instanceof ApiClientError && advance.error.isConflict;

  return (
    <>
      <PageHeader
        title="Work board"
        description="Every job in the shop, by stage. Advancing a lane records who moved it and when. Scroll or drag sideways to see every lane."
        action={
          <div className="hidden gap-1 sm:flex">
            <button
              type="button"
              onClick={() => scrollBy(-320)}
              aria-label="Scroll lanes left"
              className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
            >
              ←
            </button>
            <button
              type="button"
              onClick={() => scrollBy(320)}
              aria-label="Scroll lanes right"
              className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
            >
              →
            </button>
          </div>
        }
      />

      {advance.error && (
        <div className="mb-4">
          {conflict ? (
            <Notice tone="warn">
              {(advance.error as Error).message}{" "}
              <button
                className="font-medium underline"
                onClick={() => void queryClient.invalidateQueries({ queryKey: ["board"] })}
              >
                Reload the board
              </button>
            </Notice>
          ) : (
            <ErrorBanner error={advance.error} />
          )}
        </div>
      )}

      <DataState loading={board.isLoading} error={board.error}>
        <div
          ref={scrollerRef}
          className="scroll-x-visible flex gap-4 pb-4"
          role="region"
          aria-label="Work board lanes, scrollable horizontally"
          tabIndex={0}
          // Lets a plain vertical mouse wheel scroll the board sideways, so
          // the shop's own users are not required to discover shift+scroll.
          onWheel={(e) => {
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
              e.currentTarget.scrollLeft += e.deltaY;
            }
          }}
        >
          {board.data?.lanes.map((lane) => (
            <div key={lane.status} className="scroll-snap-item w-72 shrink-0">
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-ink">{titleCase(lane.status)}</h2>
                <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600">
                  {lane.orders.length}
                </span>
              </div>

              <div className="space-y-2">
                {lane.orders.length === 0 && (
                  <div className="rounded-lg border border-dashed border-slate-300 p-4 text-center text-xs text-slate-400">
                    Empty
                  </div>
                )}

                {lane.orders.map((ro) => {
                  const next = NEXT_LANE[ro.status];
                  const openTasks = ro.tasks?.filter((t) => t.status !== "DONE").length ?? 0;
                  const overdue =
                    ro.promisedAt && new Date(ro.promisedAt).getTime() < Date.now();

                  return (
                    <article
                      key={ro.id}
                      className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <Link
                          to={`/jobs/${ro.id}`}
                          className="font-mono text-xs font-semibold text-teal-700 hover:underline"
                        >
                          {ro.number}
                        </Link>
                        {overdue && (
                          <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-800">
                            past promise
                          </span>
                        )}
                      </div>

                      <p className="mt-1 text-sm font-medium text-ink">
                        {vehicleLabel(ro.vehicle)}
                      </p>
                      <p className="text-xs text-slate-500">{customerLabel(ro.customer)}</p>
                      <p className="mt-2 line-clamp-2 text-xs text-slate-600">{ro.concern}</p>

                      <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
                        <span>{openTasks > 0 ? `${openTasks} task(s) open` : "Tasks done"}</span>
                        <span>{ro.promisedAt ? dateTime(ro.promisedAt) : "No promise time"}</span>
                      </div>

                      {can("jobs.write") && next && (
                        <button
                          type="button"
                          onClick={() =>
                            advance.mutate({ id: ro.id, status: next, version: ro.version })
                          }
                          disabled={advance.isPending}
                          className="mt-3 w-full rounded border border-slate-300 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        >
                          Move to {titleCase(next)}
                        </button>
                      )}
                    </article>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </DataState>
    </>
  );
}
